import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { HStack, Grid, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_stacktrace/development-tickets/add-select/')({
  component: TicketTypeSelection
});
type CommonProps = {
  selectedTicketType: string | null;
  setSelectedTicketType: Dispatch<SetStateAction<string | null>>;
};
function TicketTypeSelection() {
  const [selectedTicketType, setSelectedTicketType] = useState<string | null>('ticket');
  return <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <TicketTypeRadioGroup selectedTicketType={selectedTicketType} setSelectedTicketType={setSelectedTicketType} />
      <Spacer variant="xxl" />
      <AdditionalOptions />
    </VStack>;
}
function Header() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.addSelect'
  });
  return <VStack>
      <Spacer variant="xxl" />
      <Text variant="h2">{t('pageTitle')}</Text>
    </VStack>;
}
function TicketTypeRadioGroup({
  selectedTicketType,
  setSelectedTicketType
}: CommonProps) {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.addSelect'
  });
  const TICKET_TYPES: {
    id: string;
    title: string;
    requiredDetails: {
      iconName: IconName;
      text: string;
    }[];
  }[] = [{
    id: 'ticket',
    title: t('ticketCardTitle'),
    requiredDetails: [{
      iconName: 'SquareDot',
      text: t('requiredFields.title')
    }, {
      iconName: 'SquareDot',
      text: t('requiredFields.product')
    }, {
      iconName: 'SquareDot',
      text: t('requiredFields.status')
    }]
  }];
  const navigate = useNavigate();
  const handleRadioChange = useCallback((value: string) => {
    setSelectedTicketType(value);
  }, [setSelectedTicketType]);
  const handleButtonClick = useCallback(() => {
    if (selectedTicketType) {
      navigate({
        to: '/development-tickets/add'
      });
    }
  }, [selectedTicketType, navigate]);
  return <>
      <Spacer variant="xl" />
      <Text variant="h4">{t('selectionTitle')}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange} defaultValue="ticket">
        <Grid columns={2} rowGap={28} columnGap={24}>
          {TICKET_TYPES.map(type => <AttributeCard key={type.id} title={type.title} titleStyle="CardHeader" onClick={() => handleRadioChange(type.id)} actionButton={<RadioGroupItem id={type.id} value={type.id} className="h-4 w-4" checked={selectedTicketType === type.id} />} actionButtonPosition="top-left" actionButtonIsInner={false} className="cursor-pointer rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
              <VStack className="gap-3">
                <Text variant="labelSmall">{t('requiredItems')}</Text>
                {type.requiredDetails.map((detail, index) => <Icon key={`${detail.text}-${index}`} iconName={detail.iconName} text={detail.text} />)}
              </VStack>
            </AttributeCard>)}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton icon="FilePenIcon" className="w-48" onClick={handleButtonClick}>
        {t('fillOutDetails')}
      </ActionButton>
    </>;
}
function AdditionalOptions() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.addSelect'
  });
  const navigate = useNavigate();
  const handleCsvUploadClick = useCallback(() => {
    navigate({
      to: '/development-tickets/import-csv'
    });
  }, [navigate]);
  const handleExternalSyncClick = useCallback(() => {
    navigate({
      to: '/development-tickets/external-integrations'
    });
  }, [navigate]);
  return <VStack className="w-full">
      <Text variant="labelSemiLarge">{t('orLabel')}</Text>
      <Spacer size={24} />
      <VStack className="w-full">
        {/* CSV 一括アップロード - 押下で CSV 取り込み画面へ遷移
            (BE: GET/POST /v2/pn-force/tickets/csv)。不具合 (issues) と同方針。 */}
        <Button variant="ghost" className="relative w-full justify-start p-0" onClick={handleCsvUploadClick}>
          <HStack className="w-full items-center justify-between">
            <div className="flex-1 min-w-0">
              <Icon iconName="DatabaseBackup" text={t('csvBulkUpload')} textColor="text-primary-on-neutral-fill" colorVariant="primary" />
            </div>
            <Icon iconName="ArrowRightIcon" className="m-[10px] shrink-0" />
          </HStack>
        </Button>

        <Spacer size={24} />

        {/* 外部システム同期 - 押下で連携済みの外部システム一覧画面へ直接遷移 */}
        <Button variant="ghost" className="relative w-full justify-start p-0" onClick={handleExternalSyncClick}>
          <HStack className="w-full items-center justify-between">
            <div className="flex-1 min-w-0">
              <Icon iconName="DatabaseBackup" text={t('externalSystemSync')} textColor="text-primary-on-neutral-fill" colorVariant="primary" />
            </div>
            <Icon iconName="ArrowRightIcon" className="m-[10px] shrink-0" />
          </HStack>
        </Button>
      </VStack>
    </VStack>;
}